import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Spinner } from './Spinner'
import { cx } from './cx'
import s from './Button.module.css'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  asChild?: boolean
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, asChild = false, icon, className, children, disabled, ...rest },
  ref,
) {
  /* ICON-ONLY — a glyph and an accessible name, no words — is the one button
     the coarse floor has to square (MB-SOLUM #1863 c.124186; see `.iconOnly`).
     Derived rather than a prop: a button with no children IS icon-only, and a
     prop could disagree with what the button renders. */
  const iconOnly = !asChild && icon != null && (children == null || children === false)
  const cls = cx(s.btn, s[variant], s[size], iconOnly && s.iconOnly, className)
  if (asChild) {
    return <Slot ref={ref} className={cls} {...rest}>{children}</Slot>
  }
  return (
    <button ref={ref} className={cls} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon}
      {children}
    </button>
  )
})
